import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Users,
  DollarSign,
  Clock,
  MapPin,
  Award,
  BookOpen,
  Target,
  AlertTriangle,
  CheckCircle,
  Info,
  ArrowRight,
  Filter,
  Search,
  Calendar,
  Globe,
  Zap
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface AdmissionTrend {
  year: string;
  acceptanceRate: number;
  applicants: number;
  enrolled: number;
  averageGPA: number;
  averageSAT: number;
  trend: 'up' | 'down' | 'stable';
}

interface UniversityData {
  id: string;
  name: string;
  ranking: number;
  location: string;
  type: 'public' | 'private';
  size: 'small' | 'medium' | 'large';
  acceptanceRate: number;
  averageGPA: number;
  averageSAT: number;
  tuition: number;
  financialAid: number;
  graduationRate: number;
  employmentRate: number;
  averageSalary: number;
  trends: AdmissionTrend[];
  competitiveFactors: {
    essays: number;
    extracurriculars: number;
    leadership: number;
    research: number;
    community: number;
  };
  recentChanges: string[];
  keyStrengths: string[];
  admissionTips: string[];
  deadlines: {
    early: string;
    regular: string;
    scholarships: string;
  };
}

const universityData: UniversityData[] = [
  {
    id: 'mit',
    name: 'Massachusetts Institute of Technology',
    ranking: 2,
    location: 'Cambridge, MA',
    type: 'private',
    size: 'medium',
    acceptanceRate: 4.1,
    averageGPA: 4.17,
    averageSAT: 1570,
    tuition: 57590,
    financialAid: 73,
    graduationRate: 95,
    employmentRate: 89,
    averageSalary: 126000,
    trends: [
      { year: '2024', acceptanceRate: 4.1, applicants: 33240, enrolled: 1365, averageGPA: 4.17, averageSAT: 1570, trend: 'down' },
      { year: '2023', acceptanceRate: 4.8, applicants: 31926, enrolled: 1532, averageGPA: 4.15, averageSAT: 1565, trend: 'down' },
      { year: '2022', acceptanceRate: 6.7, applicants: 28932, enrolled: 1940, averageGPA: 4.12, averageSAT: 1555, trend: 'stable' }
    ],
    competitiveFactors: {
      essays: 95,
      extracurriculars: 92,
      leadership: 88,
      research: 97,
      community: 85
    },
    recentChanges: [
      'Eliminated SAT/ACT requirement through 2026',
      'Increased focus on maker portfolios',
      'New emphasis on equity and inclusion in admissions'
    ],
    keyStrengths: ['STEM Excellence', 'Innovation Culture', 'Entrepreneurship', 'Research Opportunities'],
    admissionTips: [
      'Demonstrate genuine passion for science/engineering',
      'Show evidence of building or creating things',
      'Highlight collaborative problem-solving experiences'
    ],
    deadlines: {
      early: 'November 1',
      regular: 'January 1',
      scholarships: 'January 1'
    }
  },
  {
    id: 'harvard',
    name: 'Harvard University',
    ranking: 3,
    location: 'Cambridge, MA',
    type: 'private',
    size: 'medium',
    acceptanceRate: 3.4,
    averageGPA: 4.18,
    averageSAT: 1580,
    tuition: 56550,
    financialAid: 70,
    graduationRate: 97,
    employmentRate: 91,
    averageSalary: 134000,
    trends: [
      { year: '2024', acceptanceRate: 3.4, applicants: 61220, enrolled: 2082, averageGPA: 4.18, averageSAT: 1580, trend: 'down' },
      { year: '2023', acceptanceRate: 3.8, applicants: 57435, enrolled: 2183, averageGPA: 4.16, averageSAT: 1575, trend: 'down' },
      { year: '2022', acceptanceRate: 4.6, applicants: 52840, enrolled: 2430, averageGPA: 4.14, averageSAT: 1570, trend: 'down' }
    ],
    competitiveFactors: {
      essays: 98,
      extracurriculars: 95,
      leadership: 96,
      research: 90,
      community: 93
    },
    recentChanges: [
      'Test-optional policy extended indefinitely',
      'Increased financial aid for middle-income families',
      'Enhanced focus on first-generation college students'
    ],
    keyStrengths: ['Academic Prestige', 'Alumni Network', 'Liberal Arts Excellence', 'Global Recognition'],
    admissionTips: [
      'Craft compelling personal narrative',
      'Demonstrate intellectual curiosity across disciplines',
      'Show impact in your community or field'
    ],
    deadlines: {
      early: 'November 1',
      regular: 'January 1',
      scholarships: 'January 1'
    }
  },
  {
    id: 'stanford',
    name: 'Stanford University',
    ranking: 5,
    location: 'Stanford, CA',
    type: 'private',
    size: 'medium',
    acceptanceRate: 3.9,
    averageGPA: 4.18,
    averageSAT: 1570,
    tuition: 61731,
    financialAid: 78,
    graduationRate: 94,
    employmentRate: 93,
    averageSalary: 142000,
    trends: [
      { year: '2024', acceptanceRate: 3.9, applicants: 55471, enrolled: 2163, averageGPA: 4.18, averageSAT: 1570, trend: 'stable' },
      { year: '2023', acceptanceRate: 4.3, applicants: 52857, enrolled: 2274, averageGPA: 4.17, averageSAT: 1565, trend: 'down' },
      { year: '2022', acceptanceRate: 5.1, applicants: 47450, enrolled: 2420, averageGPA: 4.15, averageSAT: 1560, trend: 'down' }
    ],
    competitiveFactors: {
      essays: 96,
      extracurriculars: 94,
      leadership: 97,
      research: 93,
      community: 91
    },
    recentChanges: [
      'New emphasis on entrepreneurial experience',
      'Expanded need-based aid program',
      'Increased focus on climate and sustainability initiatives'
    ],
    keyStrengths: ['Innovation Hub', 'Tech Industry Connections', 'Entrepreneurship', 'Interdisciplinary Learning'],
    admissionTips: [
      'Showcase innovative thinking and problem-solving',
      'Demonstrate leadership with measurable impact',
      'Connect your interests to Stanford\'s unique offerings'
    ],
    deadlines: {
      early: 'November 1',
      regular: 'January 5',
      scholarships: 'December 1'
    }
  },
  {
    id: 'uc-berkeley',
    name: 'University of California, Berkeley',
    ranking: 20,
    location: 'Berkeley, CA',
    type: 'public',
    size: 'large',
    acceptanceRate: 11.4,
    averageGPA: 4.25,
    averageSAT: 1520,
    tuition: 14312,
    financialAid: 68,
    graduationRate: 92,
    employmentRate: 88,
    averageSalary: 118000,
    trends: [
      { year: '2024', acceptanceRate: 11.4, applicants: 128226, enrolled: 14619, averageGPA: 4.25, averageSAT: 1520, trend: 'down' },
      { year: '2023', acceptanceRate: 14.5, applicants: 125816, enrolled: 18242, averageGPA: 4.23, averageSAT: 1515, trend: 'down' },
      { year: '2022', acceptanceRate: 16.9, applicants: 112334, enrolled: 18987, averageGPA: 4.20, averageSAT: 1510, trend: 'stable' }
    ],
    competitiveFactors: {
      essays: 90,
      extracurriculars: 88,
      leadership: 85,
      research: 91,
      community: 89
    },
    recentChanges: [
      'Permanent test-blind admissions policy',
      'Increased emphasis on Personal Insight Questions',
      'New holistic review process implementation'
    ],
    keyStrengths: ['Research Excellence', 'Public Service Mission', 'Diversity', 'STEM Programs'],
    admissionTips: [
      'Focus heavily on Personal Insight Questions',
      'Demonstrate commitment to public service',
      'Show how you\'ll contribute to campus diversity'
    ],
    deadlines: {
      early: 'N/A',
      regular: 'November 30',
      scholarships: 'March 2'
    }
  }
];

export const UniversityInsights: React.FC = () => {
  const [selectedUniversity, setSelectedUniversity] = useState<UniversityData>(universityData[0]);
  const [viewMode, setViewMode] = useState<'trends' | 'comparison' | 'factors'>('trends');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUniversities = universityData.filter(uni =>
    uni.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="text-green-500" size={16} />;
      case 'down': return <TrendingDown className="text-red-500" size={16} />;
      default: return <BarChart3 className="text-blue-500" size={16} />;
    }
  };

  const getCompetitivenessLevel = (rate: number) => {
    if (rate < 5) return { level: 'Extremely Competitive', color: 'red', description: 'Top 1% of applicants' };
    if (rate < 15) return { level: 'Highly Competitive', color: 'orange', description: 'Top 15% of applicants' };
    if (rate < 30) return { level: 'Very Competitive', color: 'yellow', description: 'Top 30% of applicants' };
    return { level: 'Competitive', color: 'green', description: 'Strong applicant pool' };
  };

  const competitiveness = getCompetitivenessLevel(selectedUniversity.acceptanceRate);

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Competitive University Insights</h2>
          <p className="text-primary-100 text-lg mb-6">
            Real-time data and AI-powered analysis of top university admission trends
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm text-primary-100">Universities Tracked</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">Live</div>
              <div className="text-sm text-primary-100">Real-time Updates</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-sm text-primary-100">Powered Analysis</div>
            </div>
          </div>
        </div>
      </Card>

      {/* University Selector */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search universities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="flex gap-2">
            {['trends', 'comparison', 'factors'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === mode
                    ? 'bg-primary-100 text-primary-700 border border-primary-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {filteredUniversities.map((university) => (
            <button
              key={university.id}
              onClick={() => setSelectedUniversity(university)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedUniversity.id === university.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900 text-sm">{university.name}</h3>
                <Badge variant={university.type === 'private' ? 'primary' : 'secondary'} size="sm">
                  #{university.ranking}
                </Badge>
              </div>
              <div className="text-xs text-gray-600 mb-2">{university.location}</div>
              <div className="flex items-center justify-between text-sm">
                <span className={`font-medium ${
                  university.acceptanceRate < 5 ? 'text-red-600' :
                  university.acceptanceRate < 15 ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {university.acceptanceRate}% admit
                </span>
                {getTrendIcon(university.trends[0].trend)}
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* University Details */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedUniversity.id + viewMode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="space-y-6"
        >
          {/* University Overview */}
          <Card>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedUniversity.name}</h2>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-1">
                    <MapPin size={14} />
                    <span>{selectedUniversity.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users size={14} />
                    <span>{selectedUniversity.size} campus</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Award size={14} />
                    <span>#{selectedUniversity.ranking} ranked</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <Badge
                  variant={competitiveness.color as any}
                  className="mb-2"
                >
                  {competitiveness.level}
                </Badge>
                <div className="text-sm text-gray-600">{competitiveness.description}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">
                  {selectedUniversity.acceptanceRate}%
                </div>
                <div className="text-xs text-gray-600">Acceptance Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {selectedUniversity.averageGPA}
                </div>
                <div className="text-xs text-gray-600">Average GPA</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {selectedUniversity.averageSAT}
                </div>
                <div className="text-xs text-gray-600">Average SAT</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  ${(selectedUniversity.tuition / 1000).toFixed(0)}K
                </div>
                <div className="text-xs text-gray-600">Annual Tuition</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600 mb-1">
                  {selectedUniversity.financialAid}%
                </div>
                <div className="text-xs text-gray-600">Receive Aid</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-600 mb-1">
                  {selectedUniversity.graduationRate}%
                </div>
                <div className="text-xs text-gray-600">Graduation Rate</div>
              </div>
            </div>
          </Card>

          {/* Dynamic Content Based on View Mode */}
          {viewMode === 'trends' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Admission Trends */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Admission Trends</h3>
                <div className="space-y-4">
                  {selectedUniversity.trends.map((trend, index) => (
                    <div key={trend.year} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="text-sm font-medium text-gray-900">{trend.year}</div>
                        {getTrendIcon(trend.trend)}
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <div>{trend.acceptanceRate}% admit</div>
                        <div>{(trend.applicants / 1000).toFixed(0)}K apps</div>
                        <div>GPA {trend.averageGPA}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Recent Changes */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Changes</h3>
                <div className="space-y-3">
                  {selectedUniversity.recentChanges.map((change, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                      <AlertTriangle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{change}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {viewMode === 'factors' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Competitive Factors */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Competitive Factors</h3>
                <div className="space-y-4">
                  {Object.entries(selectedUniversity.competitiveFactors).map(([factor, score]) => (
                    <div key={factor}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700 capitalize">
                          {factor.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">{score}%</span>
                      </div>
                      <ProgressBar
                        progress={score}
                        color={score > 90 ? 'secondary' : score > 80 ? 'accent' : 'primary'}
                        size="sm"
                      />
                    </div>
                  ))}
                </div>
              </Card>

              {/* Admission Tips */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Admission Tips</h3>
                <div className="space-y-3">
                  {selectedUniversity.admissionTips.map((tip, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <Zap size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{tip}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3">Key Deadlines</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Early Decision:</span>
                      <span className="font-medium">{selectedUniversity.deadlines.early}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Regular Decision:</span>
                      <span className="font-medium">{selectedUniversity.deadlines.regular}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Scholarships:</span>
                      <span className="font-medium">{selectedUniversity.deadlines.scholarships}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Key Strengths */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">University Strengths</h3>
            <div className="flex flex-wrap gap-2">
              {selectedUniversity.keyStrengths.map((strength, index) => (
                <Badge key={index} variant="accent" className="px-3 py-2">
                  {strength}
                </Badge>
              ))}
            </div>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* AI Insights */}
      <Card className="bg-gradient-to-r from-accent-50 to-primary-50">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-accent-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              AI Analysis for {selectedUniversity.name}
            </h3>
            <p className="text-gray-700 mb-4">
              Based on current trends and your profile, here are personalized insights:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Competition is increasing - acceptance rate dropped {(selectedUniversity.trends[1].acceptanceRate - selectedUniversity.trends[0].acceptanceRate).toFixed(1)}% this year
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Research experience is becoming increasingly important for admission
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight size={16} className="text-accent-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  Strong essays can significantly boost your chances given the holistic review process
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};